import type { ReactElement } from 'react';
import { NavLink, useMatch } from 'react-router';

import { cn } from '@/components/ui/cn';
import { useDocumentHealth } from '@/features/sgc-dashboard/use-document-health';
import { useCanOpenDocuments, usePermission } from '@/hooks/usePermission';
import { plural } from '@/lib/plural';
import { useUnreadBadge } from '@/hooks/useUnreadNotifications';
import { UNREAD_UNKNOWN } from '@/lib/unread-badge';
import { useMe } from '@/lib/me';

import { NavIcon, type NavIconName } from './nav-icons';
import { LmsAdminLink } from './LmsAdminLink';
import { SidebarHealth } from './SidebarHealth';

interface NavEntry {
  icon: NavIconName;
  label: string;
  to?: string; // presente si la pantalla ya existe (navegable)
  // Si se declara, el ítem solo aparece con alguno de estos permisos.
  permission?: string | readonly string[];
}

const GESTION: NavEntry[] = [
  { icon: 'dashboard', label: 'Panel del QMS', to: '/dashboard' },
  {
    icon: 'lista',
    label: 'Lista Maestra',
    to: '/documentos',
    permission: 'quality:master-list:read',
  },
  // La Ficha y el Editor son vistas contextuales de un documento (se abren desde la
  // Lista Maestra → documento), no destinos globales: no van en el menú principal.
  {
    icon: 'tiempos',
    label: 'Estudio de tiempos',
    to: '/estudio-de-tiempos',
    permission: ['quality:productivity:read', 'quality:rework:read', 'quality:time-standards:read'],
  },
  {
    icon: 'capacitaciones',
    label: 'Capacitaciones',
    to: '/capacitaciones',
    permission: 'quality:training:read',
  },
  {
    icon: 'marca',
    label: 'Marca de agua e impresión',
    to: '/marca-de-agua',
    permission: [
      'quality:document:print',
      'quality:document:watermark-config',
      'quality:print-log:read',
    ],
  },
];

const CONFIG: NavEntry[] = [
  { icon: 'notificaciones', label: 'Notificaciones', to: '/notificaciones' },
  {
    icon: 'trazabilidad',
    label: 'Trazabilidad',
    to: '/configuracion/trazabilidad',
    permission: 'system:audit:read',
  },
  {
    icon: 'usuarios',
    label: 'Usuarios',
    to: '/configuracion/usuarios',
    permission: 'system:users:manage',
  },
  {
    icon: 'roles',
    label: 'Roles y accesos',
    to: '/configuracion/roles',
    permission: 'system:roles:manage',
  },
  {
    icon: 'catalogos',
    label: 'Catálogos',
    to: '/configuracion/catalogos',
    permission: 'quality:catalogs:manage',
  },
];

/** Contador de un ítem del menú: texto visible y nombre accesible del enlace. */
interface NavCounter {
  text: string;
  ariaLabel: string;
  alert: boolean;
}

function NavItem({
  entry,
  onNavigate,
  pending = false,
  counter = null,
}: {
  entry: NavEntry;
  onNavigate?: () => void;
  /** Contador (no leídos, documentos controlados); null = sin contador. */
  counter?: NavCounter | null;
  /** Permisos aún cargando: el ítem ocupa su lugar atenuado, sin navegar. */
  pending?: boolean;
}): ReactElement {
  const inner = (
    <>
      <NavIcon name={entry.icon} />
      {entry.label}
      {counter && (
        <span className={cn('n', counter.alert && 'alert')} aria-hidden="true">
          {counter.text}
        </span>
      )}
    </>
  );
  // La ruta actual se marca activa también mientras carga (sin parpadeo al resolver).
  const active = useMatch(entry.to ?? '/__sin-ruta__') !== null;
  if (pending) {
    return (
      <span className={cn('nav-pending', active && 'on')} aria-hidden="true">
        {inner}
      </span>
    );
  }
  // Pantalla aún no construida: se muestra idéntica al mockup (link inactivo)
  // pero no navega (evita rutas 404). Cuando exista, recibe `to` y navega.
  if (!entry.to) {
    return (
      <span aria-disabled="true" title="Disponible en una próxima entrega">
        {inner}
      </span>
    );
  }
  return (
    <NavLink
      to={entry.to}
      className={({ isActive }) => cn(isActive && 'on')}
      onClick={onNavigate}
      aria-label={counter?.ariaLabel}
    >
      {inner}
    </NavLink>
  );
}

/** Contenido del sidebar (marca + grupos de navegación). Reusado por el sidebar
 *  permanente de escritorio y por el drawer móvil, para una sola fuente de nav.
 *  `onNavigate` (opcional) se dispara al activar un enlace: el drawer lo usa
 *  para cerrarse, incluso cuando el destino es la ruta actual. */
export function SidebarContent({ onNavigate }: { onNavigate?: () => void } = {}): ReactElement {
  // Permisos resueltos por entrada. Un ítem con `permission` solo se muestra si
  // está concedido; un permiso no mapeado se oculta (fail-closed).
  const granted: Record<string, boolean> = {
    // Lista Maestra: también para el proveedor con acceso temporal (ve solo lo concedido).
    'quality:master-list:read': useCanOpenDocuments(),
    'system:users:manage': usePermission('system:users:manage'),
    'system:roles:manage': usePermission('system:roles:manage'),
    'system:audit:read': usePermission('system:audit:read'),
    'quality:catalogs:manage': usePermission('quality:catalogs:manage'),
    'quality:training:read': usePermission('quality:training:read'),
    'quality:document:print': usePermission('quality:document:print'),
    'quality:document:watermark-config': usePermission('quality:document:watermark-config'),
    'quality:print-log:read': usePermission('quality:print-log:read'),
    'quality:productivity:read': usePermission('quality:productivity:read'),
    'quality:rework:read': usePermission('quality:rework:read'),
    'quality:time-standards:read': usePermission('quality:time-standards:read'),
  };
  // Mientras /me carga, los ítems con permiso ocupan su lugar atenuados (sin saltos del
  // menú); resuelto, se muestran si están concedidos. Si /me falla se ocultan.
  const me = useMe();
  const unread = useUnreadBadge();
  const health = useDocumentHealth();
  const counterOf = (e: NavEntry): NavCounter | null => {
    if (e.icon === 'notificaciones' && unread.unknown) {
      return { text: '?', ariaLabel: `${e.label}, ${UNREAD_UNKNOWN.toLowerCase()}`, alert: false };
    }
    if (e.icon === 'notificaciones' && unread.label) {
      return { text: unread.label, ariaLabel: `${e.label}, ${unread.count} sin leer`, alert: true };
    }
    if (e.icon === 'lista' && health) {
      return {
        text: String(health.controlled),
        ariaLabel: `${e.label}, ${plural(health.controlled, 'documento controlado', 'documentos controlados')}`,
        alert: false,
      };
    }
    return null;
  };
  const loading = me.data === undefined && !me.isError;
  const visible = (e: NavEntry): boolean =>
    !e.permission ||
    loading ||
    (typeof e.permission === 'string' ? [e.permission] : e.permission).some(
      (p) => granted[p] === true,
    );
  const pending = (e: NavEntry): boolean => Boolean(e.permission) && loading;

  return (
    <>
      <div className="lockup">
        <span className="grupo">GRUPO</span>
        <span className="dim">DiM</span>
        <span className="tag">INNOVACIÓN EN UN MUNDO CAMBIANTE</span>
      </div>

      <div className="navlab">Gestión Documental</div>
      <nav className="nav" aria-label="Gestión Documental">
        {GESTION.filter(visible).map((e) => (
          <NavItem
            key={e.label}
            entry={e}
            onNavigate={onNavigate}
            pending={pending(e)}
            counter={counterOf(e)}
          />
        ))}
      </nav>

      <div className="navlab">Configuración</div>
      <nav className="nav" aria-label="Configuración">
        {CONFIG.filter(visible).map((e) => (
          <NavItem
            key={e.label}
            entry={e}
            onNavigate={onNavigate}
            pending={pending(e)}
            counter={counterOf(e)}
          />
        ))}
        <LmsAdminLink />
      </nav>

      <div className="spacer" />
      {health && <SidebarHealth health={health} />}
    </>
  );
}
